/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  SearchBar,
  Carousel,
  Button,
  Dropdown,
  Icon,
  Actionable,
  Divider,
} from '@az/starc-ui';
import { Close } from '@az/starc-ui-icons';
import styles from './styles.module.scss';
import RatingFilter from '../RatingFilter';
import { useLabels } from '@/hooks/useLabels';
import { useReviewFilters } from '../../context/ReviewFiltersProvider';
import { formatNumber } from '../../utils/formatNumber';
import { useBvPixelTracking } from '../../hooks/useBvPixelTracking';
import SortByDropDown from './SortByDropdown';
import cx from 'classnames';
import type { SortAttributeType } from '../../interface/types';
import { ChipTag } from '@/components/Filter/ChipTag';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useReviewSummary } from '../../api/getReviewSummary';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { keywordFiltersLimit } from './constants';
import { useLocale } from '@/hooks/useLocale';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

type ReviewsFilterProps = {
  onVerifiedFilter?: (verified: boolean) => void;
  onSortChange?: (value: string) => void;
  onSearchChange?: (value: string) => void;
  onKeywordSelect?: (keyword: string) => void;
  initialSelectedFilters?: number[];
  initialVerifiedSelected?: boolean;
  totalReviews?: number;
  selectedKeyword?: string;
};

const labelMap = {
  searchResultsSearch: 'label_Search_Results_Search',
  searchTopicsAndReviews: 'label_search_topics_and_reviews',
  star: 'label_reviews_star',
  stars: 'label_reviews_stars',
  singleReview: 'label_single_review',
  singleRecommendation: 'label_single_recommendation',
  reviews: 'label_reviews',
  lookingForSomethingSpecific: 'label_looking_for_something_specific',
  sortBy: 'label_reviews_sortBy',
  filterBy: 'label_reviews_filterBy',
  clearAll: 'label_reviews_clearAll',
  showReviewsThatMention: 'label_show_reviews_that_mention',
  verifiedPurchase: 'label_reviews_verifiedPurchase',
  allRatings: 'label_reviews_allRatings',
  mostRecent: 'label_reviews_sorting_mostRecent',
  mostHelpful: 'label_reviews_sorting_mostHelpful',
  clear: 'label_Search_Results_Clear',
};

const ReviewsFilter = ({
  onVerifiedFilter,
  onSortChange,
  onSearchChange,
  onKeywordSelect,
  initialVerifiedSelected = false,
  totalReviews,
  selectedKeyword,
}: ReviewsFilterProps) => {
  const { selectedRatings, setSelectedRatings } = useReviewFilters();
  const [open, setOpen] = useState(false);
  const [isVerifiedSelected, setIsVerifiedSelected] = useState(initialVerifiedSelected);
  const [searchText, setSearchText] = useState('');
  const labels = useLabels(labelMap);
  const { trackEvent } = useBvPixelTracking();
  const [isSearchBarActive, setIsSearchBarActive] = useState(false);
  const reviewCount = Number(totalReviews);
  const formattedReviewCount = formatNumber(reviewCount);
  const pdpRedesignEnabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');
  const keywordsContainerRef = useRef<HTMLUListElement>(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const locale = useLocale();
  const isUS = locale === 'en-US';
  const keywordsLimit = isUS ? keywordFiltersLimit : 0;

  const { data: productDetails } = useProductDetails();
  const skuNumber = productDetails?.product?.itemId ?? '';
  const { data: reviewSummary } = useReviewSummary({ skuNumber, enabled: !!skuNumber });
  const limitedKeywords = reviewSummary?.features?.slice(0, keywordsLimit) ?? [];
  const keywordIndex = limitedKeywords.findIndex(
    (keyword) => keyword.featureName === selectedKeyword,
  );
  const keywordFromList = keywordIndex >= 0 ? limitedKeywords.splice(keywordIndex, 1) : [];
  const reviewKeywords = [...keywordFromList, ...limitedKeywords];

  const sortOptions: Array<{ label: string; value: SortAttributeType }> = useMemo(
    () => [
      {
        label: labels.mostRecent,
        value: 'MOST_RECENT',
      },
      {
        label: labels.mostHelpful,
        value: 'MOST_HELPFUL',
      },
    ],
    [labels.mostRecent, labels.mostHelpful],
  );

  const resetFilters = useCallback(() => {
    setSelectedRatings([]);
    setIsVerifiedSelected(false);
    setSearchText('');
  }, [setSelectedRatings]);

  useEffect(() => {
    resetFilters();
  }, [resetFilters]);

  useEffect(() => {
    const handleFocusChange = () => {
      setIsSearchBarActive(document.activeElement?.id === 'reviewsSearchBar');
    };

    handleFocusChange();

    document.addEventListener('focusin', handleFocusChange);
    document.addEventListener('focusout', handleFocusChange);

    return () => {
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('focusout', handleFocusChange);
    };
  }, []);

  const handleRatingFilter = (star: number) => {
    const newFilters = selectedRatings.includes(star)
      ? selectedRatings.filter((s) => s !== star)
      : [...selectedRatings, star];

    setSelectedRatings(newFilters);
    trackEvent('Filter By', 'Click', 'Filter', star);
  };

  const handleVerifiedFilter = () => {
    const newValue = !isVerifiedSelected;
    setIsVerifiedSelected(newValue);
    onVerifiedFilter?.(newValue);
    if (!isVerifiedSelected) {
      trackEvent('Verified Purchase', 'Click', 'Filter');
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  const handleClearSearch = () => {
    setSearchText('');
    onSearchChange?.('');
  };

  const handleSearch = (event?: FormEvent) => {
    event?.preventDefault();
    onSearchChange?.(searchText);
    trackEvent('Search', 'Click', 'Search', searchText);
  };

  const handleKeywordSelect = (filter: string) => {
    onKeywordSelect?.(filter);
    keywordsContainerRef.current?.scrollTo({ left: 0 });
    trackEvent('Filter By', 'Click', 'Filter');
  };

  const searchButtons =
    isSearchBarActive || searchText.length > 0 ? (
      <>
        {searchText.length > 0 && (
          <Button
            onClick={handleClearSearch}
            className={styles.clearButton}
            variant="ghost"
            data-testid="review-clear-search-button"
          >
            <Icon svg={Close} className={styles.clearButtonImg} />
            <span className="sr-only">{labels.clear}</span>
          </Button>
        )}
        <div className={styles.searchButtonsDivider}>
          <div className={styles.verticalDivider} />
          <Button
            className={styles.searchBtn}
            data-testid="review-search-button"
            variant="ghost"
            type="submit"
          >
            <Text variant="text-link" className={styles.searchBtnText}>
              {labels.searchResultsSearch}
            </Text>
          </Button>
        </div>
      </>
    ) : undefined;

  return (
    <div className={styles.reviewsFilterContainer}>
      <div
        className={cx(styles.filtersContainer, {
          [styles['--oneLine']]: !pdpRedesignEnabled,
        })}
      >
        <form className={styles.searchBarContainer} onSubmit={handleSearch}>
          <Text variant="subtitle-bold" weight="bold" className={styles.reviewTitle}>
            {labels.lookingForSomethingSpecific}
          </Text>
          <SearchBar
            label={labels.searchTopicsAndReviews}
            className={cx(
              styles.searchBar,
              { [styles['--fullWidth']]: pdpRedesignEnabled },
              isSearchBarActive && searchText.length === 0
                ? styles.emptyInputStyles
                : searchText.length > 0 && styles.inputStyles,
            )}
            onValueChange={handleSearchChange}
            value={searchText}
            endElement={searchButtons}
            id="reviewsSearchBar"
          />
        </form>
        <div>
          <Carousel
            className={cx(styles.reviewCarousel, { [styles['--largeGap']]: pdpRedesignEnabled })}
            hideControls
          >
            <div>
              <Text variant="subtitle-bold" weight="bold" className={styles.reviewFilterText}>
                {labels.sortBy}
              </Text>
              <SortByDropDown sortOptions={sortOptions} onSortChange={onSortChange} />
            </div>
            <FlexGrid flexDirection="row" alignItems="flex-end" gap={2}>
              <div>
                <Text variant="subtitle-bold" weight="bold" className={styles.reviewFilterText}>
                  {labels.filterBy}
                </Text>
                <Dropdown
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  className={styles.reviewDropdown}
                  width={isMobile ? 131 : 220}
                >
                  <Dropdown.Button className={styles.dropdownButton}>
                    <Text className={styles.dropdownButtonText}>{labels.allRatings}</Text>
                  </Dropdown.Button>
                  <Dropdown.Content>
                    {open && (
                      <div>
                        <RatingFilter
                          handleFilter={handleRatingFilter}
                          selectedRatings={selectedRatings}
                        />
                      </div>
                    )}
                  </Dropdown.Content>
                </Dropdown>
              </div>

              <ChipTag
                label={labels.verifiedPurchase}
                handleFilter={handleVerifiedFilter}
                selectedChipVariant={'checkmarkRight'}
                isChecked={isVerifiedSelected}
              />
            </FlexGrid>
          </Carousel>
        </div>
      </div>
      {selectedRatings.length > 0 ? (
        <div className={styles.activeFiltersContainer}>
          {selectedRatings.map((star) => (
            <div key={star} className={styles.reviewStarButton}>
              <View direction={'row'} justify={'center'}>
                <Text variant="display-5" className={styles.reviewStarText}>
                  {`${star} ${star === 1 ? labels.star : labels.stars}`}
                </Text>
                <Actionable
                  onClick={() => {
                    const newFilters = selectedRatings.filter((s) => s !== star);
                    setSelectedRatings(newFilters);
                  }}
                >
                  <Icon svg={Close} size={5} className={styles.reviewStarIcon} />
                </Actionable>
              </View>
            </div>
          ))}
          {selectedRatings.length > 0 && (
            <View.Item>
              <Button
                variant="primary"
                onClick={() => {
                  setSelectedRatings([]);
                }}
                className={styles.clearAllButton}
              >
                <Text variant="display-5" className={styles.clearAllText}>
                  {labels.clearAll}
                </Text>
              </Button>
            </View.Item>
          )}
        </div>
      ) : null}
      {reviewKeywords.length > 0 ? (
        <View>
          <View.Item>
            <Text className={cx(styles.reviewTitle, styles.reviewKeywordTitle)}>
              {labels.showReviewsThatMention}
            </Text>
          </View.Item>
          <ul className={styles.reviewKeywordsList} ref={keywordsContainerRef}>
            {reviewKeywords.map((keyword, index) => {
              const label = `${keyword.featureName} (${keyword.totalReviewCount})`;
              const ariaLabel = `Keyword ${keyword.featureName}`;
              const isChecked = selectedKeyword === keyword.featureName;

              return (
                <li key={index}>
                  <ChipTag
                    selectedChipVariant={'checkmarkLeft'}
                    chipVariant={'slim'}
                    handleFilter={() => handleKeywordSelect(keyword.featureName)}
                    ariaLabel={ariaLabel}
                    label={label}
                    isChecked={isChecked}
                  />
                </li>
              );
            })}
          </ul>
        </View>
      ) : null}
      <Text
        variant="main-body"
        className={styles.totalReviewsText}
        attributes={{ id: 'reviews-list' }}
      >
        {`${formattedReviewCount} ${reviewCount === 1 ? labels.singleReview : labels.reviews}`}
      </Text>

      <Divider className={styles.horizontalDivider} />
    </div>
  );
};

export default ReviewsFilter;
